import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { maskPhone, maskCedula, maskRNC, unmask } from '../../utils/masks';
import ActividadEconomicaSelector from '../common/ActividadEconomicaSelector';

const Register = () => {
	const [formData, setFormData] = useState({
		username: '',
		email: '',
		password: '',
		password_confirm: '',
		first_name: '',
		last_name: '',
		telefono: '',
		cedula: '',
		rnc_empresa: '',
		empresa: '',
		actividad_economica: '',
		role: 'cliente'
	});
	const [errors, setErrors] = useState({});
	const [loading, setLoading] = useState(false);
	const { register, isAdmin, isContador } = useAuth();
	const navigate = useNavigate();

	const handleChange = (e) => {
		const { name, value } = e.target;
		let processedValue = value;
		
		// Aplicar máscaras según el campo
		if (name === 'telefono') {
			processedValue = maskPhone(value);
		} else if (name === 'cedula') {
			processedValue = maskCedula(value);
		} else if (name === 'rnc_empresa') {
			// RNC sin máscara, solo números
			processedValue = value.replace(/\D/g, '');
		}
		
		setFormData({
			...formData,
			[name]: processedValue
		});
		// Limpiar error del campo cuando el usuario empieza a escribir
		if (errors[name]) {
			setErrors({
				...errors,
				[name]: null
			});
		}
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setErrors({});
		setLoading(true);

		// Preparar datos sin máscaras para enviar al backend
		const dataToSend = {
			...formData,
			telefono: unmask(formData.telefono),
			cedula: unmask(formData.cedula),
			// rnc_empresa ya no tiene máscara, solo números
			rnc_empresa: formData.rnc_empresa
		};

		const result = await register(dataToSend);

		if (result.success) {
			// Redirigir a gestión de usuarios después de crear
			navigate('/admin/users');
		} else {
			setErrors(result.errors || {});
		}

		setLoading(false);
	};

	const getFieldError = (fieldName) => {
		if (errors[fieldName]) {
			if (Array.isArray(errors[fieldName])) {
				return errors[fieldName][0];
			}
			return errors[fieldName];
		}
		return null;
	};

	// Verificación adicional de permisos (aunque la ruta ya está protegida)
	if (!isAdmin() && !isContador()) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 via-white to-secondary/5 py-12 px-4 sm:px-6 lg:px-8">
				<div className="max-w-md w-full space-y-8 bg-white p-8 rounded-2xl shadow-xl text-center">
					<h2 className="text-2xl font-extrabold text-gray-900 mb-4">
						Acceso Denegado
					</h2>
					<p className="text-gray-600 mb-6">
						Solo los administradores pueden crear nuevas cuentas de usuario.
					</p>
					<button
						onClick={() => navigate('/admin/users')}
						className="px-4 py-2 bg-primary text-white rounded-lg hover:opacity-90 transition-opacity"
					>
						Ir a Gestión de Usuarios
					</button>
				</div>
			</div>
		);
	}

	return (
		<div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 via-white to-secondary/5 py-12 px-4 sm:px-6 lg:px-8">
			<div className="max-w-md w-full space-y-8 bg-white p-8 rounded-2xl shadow-xl">
				<div>
					<h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
						Crear Nuevo Usuario
					</h2>
					<p className="mt-2 text-center text-sm text-gray-600">
						Completa el formulario para crear una nueva cuenta de usuario
					</p>
				</div>
				<form className="mt-8 space-y-6" onSubmit={handleSubmit}>
					<div className="space-y-4">
						<div className="grid grid-cols-2 gap-4">
							<div>
								<label htmlFor="first_name" className="block text-sm font-medium text-gray-700">
									Nombre
								</label>
								<input
									id="first_name"
									name="first_name"
									type="text"
									className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
									value={formData.first_name}
									onChange={handleChange}
								/>
							</div>
							<div>
								<label htmlFor="last_name" className="block text-sm font-medium text-gray-700">
									Apellido
								</label>
								<input
									id="last_name"
									name="last_name"
									type="text"
									className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
									value={formData.last_name}
									onChange={handleChange}
								/>
							</div>
						</div>

						<div>
							<label htmlFor="username" className="block text-sm font-medium text-gray-700">
								Usuario *
							</label>
							<input
								id="username"
								name="username"
								type="text"
								required
								className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
								value={formData.username}
								onChange={handleChange}
							/>
							{getFieldError('username') && (
								<p className="mt-1 text-sm text-red-600">{getFieldError('username')}</p>
							)}
						</div>

						<div>
							<label htmlFor="email" className="block text-sm font-medium text-gray-700">
								Email *
							</label>
							<input
								id="email"
								name="email"
								type="email"
								required
								className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
								value={formData.email}
								onChange={handleChange}
							/>
							{getFieldError('email') && (
								<p className="mt-1 text-sm text-red-600">{getFieldError('email')}</p>
							)}
						</div>

						<div>
							<label htmlFor="password" className="block text-sm font-medium text-gray-700">
								Contraseña * (mínimo 8 caracteres)
							</label>
							<input
								id="password"
								name="password"
								type="password"
								required
								minLength={8}
								className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
								value={formData.password}
								onChange={handleChange}
							/>
							{getFieldError('password') && (
								<p className="mt-1 text-sm text-red-600">{getFieldError('password')}</p>
							)}
						</div>

						<div>
							<label htmlFor="password_confirm" className="block text-sm font-medium text-gray-700">
								Confirmar Contraseña *
							</label>
							<input
								id="password_confirm"
								name="password_confirm"
								type="password"
								required
								minLength={8}
								className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
								value={formData.password_confirm}
								onChange={handleChange}
							/>
							{getFieldError('password_confirm') && (
								<p className="mt-1 text-sm text-red-600">{getFieldError('password_confirm')}</p>
							)}
						</div>

						<div>
							<label htmlFor="telefono" className="block text-sm font-medium text-gray-700">
								Teléfono
							</label>
							<input
								id="telefono"
								name="telefono"
								type="tel"
								placeholder="(809) 123-4567"
								maxLength={14}
								className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
								value={formData.telefono}
								onChange={handleChange}
							/>
						</div>

						<div>
							<label htmlFor="cedula" className="block text-sm font-medium text-gray-700">
								Cédula
							</label>
							<input
								id="cedula"
								name="cedula"
								type="text"
								placeholder="001-1234567-8"
								maxLength={13}
								className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
								value={formData.cedula}
								onChange={handleChange}
							/>
						</div>

						<div>
							<label htmlFor="rnc_empresa" className="block text-sm font-medium text-gray-700">
								RNC de la Empresa
							</label>
							<input
								id="rnc_empresa"
								name="rnc_empresa"
								type="text"
								placeholder="123456789 o 12345678901"
								maxLength={11}
								className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
								value={formData.rnc_empresa}
								onChange={(e) => {
									// Solo permitir números, sin máscara
									const numbers = e.target.value.replace(/\D/g, '');
									setFormData({ ...formData, rnc_empresa: numbers });
								}}
							/>
						</div>

						<div>
							<label htmlFor="empresa" className="block text-sm font-medium text-gray-700">
								Empresa
							</label>
							<input
								id="empresa"
								name="empresa"
								type="text"
								className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
								value={formData.empresa}
								onChange={handleChange}
							/>
						</div>

						<div>
							<label htmlFor="actividad_economica" className="block text-sm font-medium text-gray-700">
								Actividad Económica
							</label>
							<ActividadEconomicaSelector
								value={formData.actividad_economica}
								onChange={(actividad) => {
									if (actividad) {
										setFormData({ 
											...formData, 
											actividad_economica: actividad.codigo + ' - ' + actividad.descripcion 
										});
									} else {
										setFormData({ ...formData, actividad_economica: '' });
									}
								}}
								placeholder="Buscar actividad económica..."
								className="mt-1"
							/>
						</div>
					</div>

					<div>
						<button
							type="submit"
							disabled={loading}
							className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
						>
							{loading ? 'Creando cuenta...' : 'Crear Cuenta'}
						</button>
					</div>
				</form>
			</div>
		</div>
	);
};

export default Register;

